import {css} from '@emotion/react'
import {useStaticQuery, graphql} from 'gatsby'
import React, {useRef, useEffect} from 'react'
import VanishLogo from './VanishLogoA'
import VanishLogoB from './VanishLogoB'
import ShieldIcon from '../../../images/vanish/icons/icn-shield.svg'
import ArrowDownIcon from '../../../images/vanish/icons/arrow-down-icon.svg'
import ConditionalDownloadCTA from './ConditionalDownloadCTA'
import SubLogo from './SubLogo'

const translations = {
    en: {
        title: "Chat freely - we're not looking",
        titleHighlight: 'Private AI',
        features: [
            {
                icon: ShieldIcon,
                text: 'Anonymous access to top AI models (GPT-5, Claude, & Sonar)',
            },
            {
                icon: ShieldIcon,
                text: 'Your conversations are never tracked',
            },
            {
                icon: ShieldIcon,
                text: 'AI chats are stored on your device, not the cloud',
            },
        ],
        bottomTag: 'Free to download and use. Subscribe for unlimited access to all features.',
        learnMore: 'Learn more',
        phoneAlt: 'A smartphone screen of the Vanish private AI chat app',
        countryDisclaimer:
            'Vanish is currently available in English, Dutch, German, and French in the US, Canada, Germany, Spain, France, the Netherlands, and the UK',
    },
    fr: {
        title: 'Discutez librement — nous ne regardons pas',
        titleHighlight: 'IA privée',
        features: [
            {
                icon: ShieldIcon,
                text: 'Accès anonyme aux meilleurs modèles d’IA (GPT-5, Claude et Sonar)',
            },
            {
                icon: ShieldIcon,
                text: 'Vos conversations ne sont jamais suivies',
            },
            {
                icon: ShieldIcon,
                text: 'Les discussions IA sont stockées sur votre appareil, pas dans le cloud',
            },
        ],
        bottomTag:
            'Gratuit à télécharger et à utiliser. Abonnez-vous pour un accès illimité à toutes les fonctionnalités.',
        learnMore: 'En savoir plus',
        phoneAlt: 'Écran de smartphone montrant l’application de chat IA privée Vanish',
        countryDisclaimer:
            'Vanish est actuellement disponible en anglais, néerlandais, allemand et français aux États-Unis, au Canada, en Allemagne, en Espagne, en France, aux Pays-Bas et au Royaume-Uni',
    },
    de: {
        title: 'Chatten Sie frei - wir schauen nicht zu',
        titleHighlight: 'Private KI',
        features: [
            {
                icon: ShieldIcon,
                text: 'Anonymer Zugang zu Top-KI-Modellen (GPT-5, Claude & Sonar)',
            },
            {
                icon: ShieldIcon,
                text: 'Ihre Gespräche werden niemals verfolgt',
            },
            {
                icon: ShieldIcon,
                text: 'KI-Chats werden auf Ihrem Gerät gespeichert, nicht in der Cloud',
            },
        ],
        bottomTag: 'Kostenlos herunterladen und nutzen. Abonnieren Sie für unbegrenzten Zugang zu allen Funktionen.',
        learnMore: 'Mehr erfahren',
        phoneAlt: 'Ein Smartphone-Bildschirm der Vanish Private-AI-Chat-App',
        countryDisclaimer:
            'Vanish ist derzeit nur auf Englisch, Niederländisch, Deutsch und Französisch in den USA, Kanada, Deutschland, Spanien, Frankreich, den Niederlanden und Großbritannien verfügbar',
    },
    nl: {
        title: 'Chat vrijuit - wij kijken niet mee',
        titleHighlight: 'Privé AI',
        features: [
            {
                icon: ShieldIcon,
                text: 'Anonieme toegang tot top AI-modellen (GPT-5, Claude & Sonar)',
            },
            {
                icon: ShieldIcon,
                text: 'Uw gesprekken worden nooit gevolgd',
            },
            {
                icon: ShieldIcon,
                text: 'AI-chats worden opgeslagen op uw apparaat, niet in de cloud',
            },
        ],
        bottomTag: 'Gratis te downloaden en te gebruiken. Abonneer u voor onbeperkte toegang tot alle functies.',
        learnMore: 'Meer informatie',
        phoneAlt: 'Een smartphonescherm van de Vanish privé-AI-chatapp',
        countryDisclaimer:
            'Vanish is momenteel beschikbaar in het Engels, Nederlands, Duits en Frans in de Verenigde Staten, Canada, Duitsland, Spanje, Frankrijk, Nederland en het Verenigd Koninkrijk',
    },
}

const VanishHero = ({languageCode = 'en', experimentId, os, browser, isMobileDevice, setShowTopBar}) => {
    const text = translations[languageCode] || translations.en
    const sectionRef = useRef(null)
    const downloadBadgeRef = useRef(null)

    const images = useStaticQuery(graphql`
        query VanishUnsupportedImages {
            desktopPhoneEn: file(relativePath: {eq: "vanish/phones/app-chat-en.png"}) {
                publicURL
            }
            desktopPhoneDe: file(relativePath: {eq: "vanish/phones/app-chat-de.png"}) {
                publicURL
            }
            desktopPhoneNl: file(relativePath: {eq: "vanish/phones/app-chat-nl.png"}) {
                publicURL
            }
            desktopPhoneFr: file(relativePath: {eq: "vanish/phones/app-chat-fr.png"}) {
                publicURL
            }
            cloudyOverlay: file(relativePath: {eq: "vanish/overlay/cloudy-overlay.svg"}) {
                publicURL
            }
        }
    `)

    const getPhoneImage = () => {
        switch (languageCode) {
            case 'de':
                return images.desktopPhoneDe.publicURL
            case 'nl':
                return images.desktopPhoneNl.publicURL
            case 'fr':
                return images.desktopPhoneFr.publicURL
            default:
                return images.desktopPhoneEn.publicURL
        }
    }

    const handleScrollPast = () => {
        if (sectionRef.current) {
            const sectionBottom = sectionRef.current.getBoundingClientRect().bottom + window.scrollY
            window.scrollTo({
                top: sectionBottom,
                behavior: 'smooth',
            })
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleScrollPast()
        }
    }

    useEffect(() => {
        const handleScroll = () => {
            if (downloadBadgeRef.current && setShowTopBar) {
                const sectionBottom = downloadBadgeRef.current.getBoundingClientRect().bottom
                // If the bottom of the section is above the top of the viewport, user has scrolled past it
                if (sectionBottom <= 0) {
                    setShowTopBar(true)
                } else {
                    // If the bottom of the section is visible, user has scrolled back up past it
                    setShowTopBar(false)
                }
            }
        }

        window.addEventListener('scroll', handleScroll)
        // Check on mount in case user is already scrolled past
        handleScroll()

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [setShowTopBar])

    return (
        <section
            ref={sectionRef}
            className="vanish-unsupported"
            css={css`
                width: 100%;
                min-height: 100vh;
                background: linear-gradient(133deg, #010102 12.16%, #8519b4 111.57%);
                background-attachment: fixed;
                position: relative;
                overflow: hidden;
                display: flex;
                flex-direction: column;

                @media (max-width: 480px) {
                    min-height: 100vh;
                    height: auto;
                    overflow: visible;
                    background: #000;
                }
            `}
        >
            <div
                css={css`
                    width: 499px;
                    height: 519px;
                    position: absolute;
                    border-radius: 519px;
                    background: rgba(85, 11, 200, 0.8);
                    filter: blur(125px);
                    top: 530px;
                    left: -185px;
                    display: none;
                    @media (max-width: 480px) {
                        display: block;
                    }
                `}
            />
            <div
                css={css`
                    width: 458.338px;
                    height: 458.338px;
                    position: absolute;
                    border-radius: 458.338px;
                    background: rgba(85, 11, 200, 0.4);
                    filter: blur(97.43590545654297px);
                    bottom: -103px;
                    display: none;
                    @media (max-width: 480px) {
                        display: block;
                    }
                `}
            />
            <div
                css={css`
                    width: 676px;
                    height: 676px;
                    position: absolute;
                    border-radius: 676px;
                    background: rgba(202, 61, 228, 0.4);
                    filter: blur(100px);
                    bottom: -106px;
                    right: -226px;
                    display: none;
                    @media (max-width: 480px) {
                        display: block;
                    }
                `}
            />
            {/* Cloudy Overlay */}
            <div
                css={css`
                    position: fixed;
                    width: 100vw;
                    height: 100vh;
                    background-image: url(${images.cloudyOverlay.publicURL});
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    opacity: 1;
                    mix-blend-mode: plus-lighter;
                    z-index: 1;
                    @media (max-width: 480px) {
                        background-position: bottom;
                    }
                `}
            />

            {/* Main Content */}
            <div
                className="main-content"
                css={css`
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    z-index: 2;
                    padding: 30px 20px 20px;

                    @media (max-width: 880px) {
                        padding: 65px 15px 15px;
                        overflow: hidden;
                    }
                    @media (max-width: 445px) {
                        padding: 40px 15px 15px;
                    }
                `}
            >
                <div
                    className="content-wrapper"
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        max-width: 1000px;
                        width: 100%;
                        gap: 30px;
                        margin: 0 15px;

                        @media (max-width: 880px) {
                            flex-direction: column;
                            text-align: left;
                            gap: 64px;
                            margin: 0 auto -210px;
                            align-items: center;
                        }
                        @media (max-width: 445px) {
                            gap: 60px;
                        }
                    `}
                >
                    {/* Left Side - Content */}
                    <div
                        className="content"
                        css={css`
                            flex: 1;
                            max-width: 615px;

                            @media (max-width: 880px) {
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                text-align: left;
                                padding: 0 20px;
                                max-width: 100%;
                            }
                        `}
                        ref={downloadBadgeRef}
                    >
                        {/* Logo */}
                        <div
                            className="title-container"
                            css={css`
                                margin-bottom: 50px;
                                @media (max-width: 880px) {
                                    margin-bottom: 40px;
                                    display: flex;
                                    flex-direction: column;
                                    align-items: center;
                                }
                                @media (max-width: 445px) {
                                    margin-bottom: 25px;
                                }
                            `}
                        >
                            <div
                                className="logo desktop"
                                css={css`
                                    display: flex;
                                    align-items: center;
                                    margin-bottom: 17px;
                                    @media (max-width: 880px) {
                                        display: none;
                                    }
                                `}
                            >
                                <VanishLogo
                                    css={css`
                                        width: 200px;
                                    `}
                                />
                            </div>
                            <div
                                className="logo mobile"
                                css={css`
                                    display: none;
                                    align-items: center;
                                    margin-bottom: 17px;
                                    @media (max-width: 880px) {
                                        display: flex;
                                    }
                                `}
                            >
                                <VanishLogoB
                                    css={css`
                                        width: 200px;
                                    `}
                                />
                            </div>
                            <SubLogo languageCode={languageCode} />
                        </div>

                        <h1
                            css={css`
                                color: var(--text-primary, #f2f3fb);
                                leading-trim: both;
                                text-edge: cap;
                                font-family: Inter;
                                font-size: 60px;
                                font-style: normal;
                                font-weight: 700;
                                line-height: 60px;
                                letter-spacing: 0.408px;
                                margin-bottom: 44px;

                                @media (max-width: 880px) {
                                    text-align: center;
                                    font-size: 42px;
                                    line-height: 40px;
                                    margin-bottom: 40px;
                                }
                                @media (max-width: 445px) {
                                    font-size: 35px;
                                    margin-bottom: 25px;
                                }
                            `}
                        >
                            {text.title}
                        </h1>

                        {/* Features List */}
                        <ul
                            css={css`
                                list-style: none;
                                padding: 0;
                                margin: 0 0 40px 0;

                                @media (max-width: 480px) {
                                    text-align: left;
                                    max-width: 400px;
                                    margin-bottom: 63px;
                                }
                                @media (max-width: 445px) {
                                    margin-bottom: 10px;
                                }
                            `}
                        >
                            {text.features.map((feature, index) => (
                                <li
                                    key={index}
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        gap: 12px;
                                        margin-bottom: 16px;
                                        line-height: 1.5;
                                    `}
                                >
                                    <feature.icon
                                        css={css`
                                            width: 24px;
                                            height: 24px;
                                            flex-shrink: 0;
                                        `}
                                    />
                                    <span
                                        css={css`
                                            color: var(--text-primary, #f2f3fb);
                                            font-family: Inter;
                                            font-size: 16px;
                                            font-style: normal;
                                            font-weight: 400;
                                            line-height: 16px; /* 88.889% */
                                            @media (max-width: 990px) {
                                                line-height: 24px; /* 133.333% */
                                            }
                                        `}
                                    >
                                        {feature.text}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <p
                            css={css`
                                color: var(--text-secondary, #b4a8e1);
                                leading-trim: both;
                                text-edge: cap;
                                font-family: Inter;
                                font-size: 16px;
                                font-style: normal;
                                font-weight: 400;
                                line-height: 24px; /* 150% */
                                margin-bottom: 16px;
                                @media (max-width: 880px) {
                                    text-align: center;
                                    margin-bottom: 63px;
                                }
                                @media (max-width: 445px) {
                                    margin-bottom: 30px;
                                }
                            `}
                        >
                            {text.bottomTag}
                        </p>

                        {/* Download Buttons */}
                        <ConditionalDownloadCTA
                            isMobileDevice={isMobileDevice}
                            languageCode={languageCode}
                            experimentId={experimentId}
                            os={os}
                            browser={browser}
                            ctaType="cta-hero"
                            mobileWrapperStyles={css`
                                align-items: center;
                                width: 100%;
                            `}
                            desktopWrapperStyles={css`
                                width: 100%;
                            `}
                        />
                        <p
                            className="countrty-disclaimer"
                            css={css`
                                color: var(--text-secondary, #b4a8e1);
                                margin: 10px 0 0;
                                font-size: 12px;
                                font-style: normal;
                                font-weight: 400;
                                line-height: 16px;
                                font-style: italic;
                            `}
                        >
                            {text.countryDisclaimer}
                        </p>
                    </div>

                    {/* Right Side - Phone Mockup */}

                    <div
                        className="phone-mockup"
                        css={css`
                            flex: 0 0 auto;
                            width: 320px;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            position: relative;

                            @media (max-width: 640px) {
                                width: 100%;
                                max-width: 250px;
                                margin: 0 auto;
                            }

                            @media (max-width: 900px) {
                                width: 100%;
                                max-width: 280px;
                            }
                        `}
                    >
                        <div
                            className="phone-mockup-wrapper"
                            css={css`
                                position: absolute;
                                top: -35px;
                                right: -250px;
                                bottom: -12px;
                                left: -217px;
                                border-radius: 776px;
                                background: rgba(85, 11, 200, 0.8);
                                filter: blur(178.10621643066406px);
                                z-index: -1;
                                @media (max-width: 480px) {
                                    display: none;
                                }
                            `}
                        />
                        <img
                            src={getPhoneImage()}
                            alt={text.phoneAlt}
                            loading="eager"
                            css={css`
                                position: relative;
                                z-index: 10;
                                width: 100%;
                                height: auto;
                                max-width: 302px;
                                filter: drop-shadow(0 15px 40px rgba(0, 0, 0, 0.4));
                            `}
                        />
                    </div>
                </div>
            </div>

            {/* Below the Fold CTA */}
            <div
                className="desktop-footer"
                onClick={handleScrollPast}
                onKeyDown={handleKeyDown}
                css={css`
                    position: relative;
                    z-index: 2;
                    padding: 30px 40px;
                    display: flex;
                    justify-content: center;
                    flex-shrink: 0;
                    flex-direction: column;
                    align-items: center;
                    width: fit-content;
                    align-self: center;
                    cursor: pointer;
                    @media (max-width: 880px) {
                        display: none;
                    }
                `}
                role="button"
                tabIndex={0}
            >
                <p
                    css={css`
                        margin-bottom: 18px;
                        color: var(--whisper-dark, #ebecf7);
                        text-align: center;
                        leading-trim: both;
                        text-edge: cap;
                        font-family: Inter;
                        font-size: 14px;
                        font-style: normal;
                        font-weight: 600;
                        line-height: 20px; /* 142.857% */
                    `}
                >
                    {text.learnMore}
                </p>
                <div
                    className="arrow-down-container"
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 6px;
                    `}
                >
                    <ArrowDownIcon />
                    <ArrowDownIcon />
                </div>
            </div>
        </section>
    )
}

export default VanishHero
